import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Avatar,
  IconButton,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  PersonAdd,
  CameraAlt,
  CloudUpload,
  Delete,
  CheckCircle,
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  Badge,
  DriveEta,
  VerifiedUser,
  Schedule,
  ContactPhone,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-hot-toast';

interface AddDriverDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (driverData: DriverFormData) => Promise<void>;
  initialData?: Partial<DriverFormData>;
  isEditing?: boolean;
  driverId?: string;
}

interface DriverFormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  profileImage?: File;
  
  // Driver Details
  licenseNumber: string;
  licenseExpiry: string;
  yearsExperience: number;
  specializations: string[];
  hourlyRate: number;
  dailyRate: number;
  
  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  
  // Availability
  preferredSchedule: 'full-time' | 'part-time' | 'flexible';
  workingHoursStart: string;
  workingHoursEnd: string;
  maxDistance: number;
  preferredAreas: string[];
  
  // Guarantor Information
  guarantorName: string;
  guarantorPhone: string;
  guarantorRelationship: string;
}

const specializations = [
  'Economy', 'Luxury', 'SUV', 'Executive', 'Airport Transfer', 'Long Distance'
];

const preferredAreas = [
  'Lagos Island', 'Victoria Island', 'Ikoyi', 'Lekki', 'Ikeja', 'Surulere', 
  'Yaba', 'Gbagada', 'Apapa', 'Mainland'
];

const relationships = [
  'Spouse', 'Sibling', 'Parent', 'Child', 'Friend', 'Colleague', 'Other'
];

const steps = [
  'Personal Information',
  'Driver License & Experience', 
  'Emergency Contact',
  'Availability & Preferences',
  'Guarantor Information',
  'Review & Submit'
];

export default function AddDriverDialog({
  open,
  onClose,
  onSubmit,
  initialData,
  isEditing = false,
  driverId
}: AddDriverDialogProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const getInitialFormData = (): DriverFormData => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    licenseNumber: '',
    licenseExpiry: '',
    yearsExperience: 0,
    specializations: [],
    hourlyRate: 0,
    dailyRate: 0,
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: 'Spouse',
    preferredSchedule: 'full-time',
    workingHoursStart: '06:00',
    workingHoursEnd: '22:00',
    maxDistance: 50,
    preferredAreas: [],
    guarantorName: '',
    guarantorPhone: '',
    guarantorRelationship: 'Spouse',
    ...initialData
  });

  const [formData, setFormData] = useState<DriverFormData>(getInitialFormData());

  // Reset form data when dialog opens with new initial data
  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
      setActiveStep(0);
    }
  }, [open, initialData]);

  const handleImageUpload = (file: File) => {
    setFormData(prev => ({ ...prev, profileImage: file }));
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, profileImage: undefined }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSpecializationToggle = (specialization: string) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(specialization)
        ? prev.specializations.filter(s => s !== specialization)
        : [...prev.specializations, specialization]
    }));
  };

  const handleAreaToggle = (area: string) => {
    setFormData(prev => ({
      ...prev,
      preferredAreas: prev.preferredAreas.includes(area)
        ? prev.preferredAreas.filter(a => a !== area)
        : [...prev.preferredAreas, area]
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (!isStepValid(activeStep)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      toast.success('Driver added successfully!');
      handleClose();
    } catch (error: any) {
      console.error('Failed to add driver:', error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error('A driver with this email or phone number already exists');
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || 'Invalid driver information. Please check your inputs.';
        toast.error(errorMsg);
      } else {
        toast.error(`Failed to add driver: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      licenseNumber: '',
      licenseExpiry: '',
      yearsExperience: 0,
      specializations: [],
      hourlyRate: 0,
      dailyRate: 0,
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelationship: 'Spouse',
      preferredSchedule: 'full-time',
      workingHoursStart: '06:00',
      workingHoursEnd: '22:00',
      maxDistance: 50,
      preferredAreas: [],
      guarantorName: '',
      guarantorPhone: '',
      guarantorRelationship: 'Spouse'
    });
    setImagePreview(null);
    onClose();
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return formData.firstName && formData.lastName && formData.email && formData.phone && formData.dateOfBirth;
      case 1:
        return formData.licenseNumber && formData.licenseExpiry && formData.yearsExperience > 0 && formData.hourlyRate > 0 && formData.dailyRate > 0;
      case 2:
        return formData.emergencyContactName && formData.emergencyContactPhone;
      case 3:
        return formData.workingHoursStart && formData.workingHoursEnd && formData.maxDistance > 0;
      case 4:
        return formData.guarantorName && formData.guarantorPhone;
      default:
        return true;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            {/* Profile Image Upload */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={imagePreview || undefined}
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    bgcolor: 'primary.main',
                    fontSize: '2rem'
                  }}
                >
                  {formData.firstName ? formData.firstName.charAt(0) : <PersonAdd />}
                </Avatar>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                  style={{ display: 'none' }}
                />
                
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CameraAlt />
                </IconButton>
                
                {imagePreview && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' }
                    }}
                    size="small"
                    onClick={removeImage}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Email /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number *"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth *"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  value={formData.zipCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Driver License Number *"
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, licenseNumber: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Badge /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="License Expiry Date *"
                  value={formData.licenseExpiry}
                  onChange={(e) => setFormData(prev => ({ ...prev, licenseExpiry: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Years of Driving Experience *"
                  value={formData.yearsExperience}
                  onChange={(e) => setFormData(prev => ({ ...prev, yearsExperience: parseInt(e.target.value) }))}
                  inputProps={{ min: 1, max: 50 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><DriveEta /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Hourly Rate (₦) *"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, hourlyRate: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ min: 0, step: 100 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>
                  }}
                  helperText="Rate per hour for driver hire services"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Daily Rate (₦) *"
                  value={formData.dailyRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dailyRate: parseFloat(e.target.value) || 0 }))}
                  inputProps={{ min: 0, step: 500 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>
                  }}
                  helperText="Rate per day for driver hire services"
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Driving Specializations</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {specializations.map((spec) => (
                  <Chip
                    key={spec}
                    label={spec}
                    variant={formData.specializations.includes(spec) ? 'filled' : 'outlined'}
                    color={formData.specializations.includes(spec) ? 'primary' : 'default'}
                    onClick={() => handleSpecializationToggle(spec)}
                    clickable
                  />
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Emergency Contact Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Name *"
                  value={formData.emergencyContactName}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactName: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><ContactPhone /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Emergency Contact Phone *"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactPhone: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Relationship</InputLabel>
                  <Select
                    value={formData.emergencyContactRelationship}
                    label="Relationship"
                    onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactRelationship: e.target.value }))}
                  >
                    {relationships.map(rel => (
                      <MenuItem key={rel} value={rel}>{rel}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Availability & Preferences</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Schedule</InputLabel>
                  <Select
                    value={formData.preferredSchedule}
                    label="Preferred Schedule"
                    onChange={(e) => setFormData(prev => ({ ...prev, preferredSchedule: e.target.value as any }))}
                  >
                    <MenuItem value="full-time">Full Time</MenuItem>
                    <MenuItem value="part-time">Part Time</MenuItem>
                    <MenuItem value="flexible">Flexible</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="Working Hours Start"
                  value={formData.workingHoursStart}
                  onChange={(e) => setFormData(prev => ({ ...prev, workingHoursStart: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="time"
                  label="Working Hours End"
                  value={formData.workingHoursEnd}
                  onChange={(e) => setFormData(prev => ({ ...prev, workingHoursEnd: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Maximum Distance (km)"
                  value={formData.maxDistance}
                  onChange={(e) => setFormData(prev => ({ ...prev, maxDistance: parseInt(e.target.value) }))}
                  inputProps={{ min: 1, max: 200 }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Preferred Areas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {preferredAreas.map((area) => (
                  <Chip
                    key={area}
                    label={area}
                    variant={formData.preferredAreas.includes(area) ? 'filled' : 'outlined'}
                    color={formData.preferredAreas.includes(area) ? 'primary' : 'default'}
                    onClick={() => handleAreaToggle(area)}
                    clickable
                  />
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Guarantor Information</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              A guarantor is required for all drivers. This person will be contacted in case of emergencies or issues.
            </Alert>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Guarantor Name *"
                  value={formData.guarantorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, guarantorName: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><VerifiedUser /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Guarantor Phone *"
                  value={formData.guarantorPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, guarantorPhone: e.target.value }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Relationship to Driver</InputLabel>
                  <Select
                    value={formData.guarantorRelationship}
                    label="Relationship to Driver"
                    onChange={(e) => setFormData(prev => ({ ...prev, guarantorRelationship: e.target.value }))}
                  >
                    {relationships.map(rel => (
                      <MenuItem key={rel} value={rel}>{rel}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 5:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Review & Confirm</Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              Please review all information before submitting. You can go back to make changes if needed.
            </Alert>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="Name" secondary={`${formData.firstName} ${formData.lastName}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Email" secondary={formData.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Phone" secondary={formData.phone} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Date of Birth" secondary={formData.dateOfBirth} />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Driver Details</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="License Number" secondary={formData.licenseNumber} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Experience" secondary={`${formData.yearsExperience} years`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Specializations" secondary={formData.specializations.join(', ') || 'None selected'} />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAdd color="primary" />
          <Typography variant="h6">{isEditing ? 'Edit Driver' : 'Add New Driver'}</Typography>
        </Box>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Show stepper only when adding new driver */}
      {!isEditing && (
        <Box sx={{ px: 3, py: 1 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        {isEditing ? (
          // When editing, show all sections in one scrollable view
          <Box sx={{ mt: 2 }}>
            {/* Personal Information */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom color="primary">Personal Information</Typography>
              {renderStepContent(0)}
            </Paper>

            {/* Driver License & Experience */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom color="primary">Driver License & Experience</Typography>
              {renderStepContent(1)}
            </Paper>

            {/* Emergency Contact */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom color="primary">Emergency Contact</Typography>
              {renderStepContent(2)}
            </Paper>

            {/* Availability & Preferences */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom color="primary">Availability & Preferences</Typography>
              {renderStepContent(3)}
            </Paper>

            {/* Guarantor Information */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom color="primary">Guarantor Information</Typography>
              {renderStepContent(4)}
            </Paper>
          </Box>
        ) : (
          // When adding, use the stepper
          renderStepContent(activeStep)
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>

        <Box sx={{ flex: 1 }} />

        {isEditing ? (
          // Simple save button when editing
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        ) : (
          // Stepper navigation when adding
          <>
            {activeStep > 0 && (
              <Button onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid(activeStep)}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting || !isStepValid(activeStep)}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : <PersonAdd />}
              >
                {isSubmitting ? 'Adding Driver...' : 'Add Driver'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}